import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useOnboardingState } from "./hooks/useOnboardingState";
import ProgressBar from "./components/ProgressBar";
import StepBusinessInfo from "./components/StepBusinessInfo";
import StepEcosystem from "./components/StepEcosystem";
import StepBusinessPortfolio from "./components/StepBusinessPortfolio";
import SuccessScreen from "./components/SuccessScreen";
import TermsModal from "./components/TermsModal";
import DataRecoveryModal from "./components/DataRecoveryModal";
import { emailRegex, phoneRegex, urlRegex } from "../../lib/validations/commonSchemas";
import { isValidStrictUrl } from "../../utils/zod/onboardingSchemas";

export default function BrandOnboarding() {
  const state = useOnboardingState();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("termsAccepted") === "1") {
      state.setTermsAccepted(true);
      state.setShowTermsModal(false);
      state.setShowSuccessScreen(true);
    }
  }, [searchParams]);

  if (state.showSuccessScreen) {
    return <SuccessScreen />;
  }

  const renderStepContent = () => {
    switch (state.currentStep) {
      case 1:
        return (
          <StepBusinessInfo
            getOnboardingData={state.getOnboardingData}
            errors={state.errors}
            fullName={state.fullName}
            username={state.username}
            usernameEditing={state.usernameEditing}
            email={state.email}
            emailOtpSent={state.emailOtpSent}
            emailOtpCode={state.emailOtpCode}
            emailVerified={state.emailVerified}
            emailOtpError={state.emailOtpError}
            emailOtpCountdown={state.emailOtpCountdown}
            brandName={state.brandName}
            verifiedCompanyName={state.verifiedCompanyName}
            websiteUrl={state.websiteUrl}
            websiteUrlStatus={state.websiteUrlStatus}
            businessDescription={state.businessDescription}
            businessType={state.businessType}
            primaryCategory={state.primaryCategory}
            showCategoryDropdown={state.showCategoryDropdown}
            isManagedByAgency={state.isManagedByAgency}
            selectedAgencyFromList={state.selectedAgencyFromList}
            showAgencyDropdown={state.showAgencyDropdown}
            agencyNotListed={state.agencyNotListed}
            agencyName={state.agencyName}
            agencyContactName={state.agencyContactName}
            agencyEmail={state.agencyEmail}
            agencyPhone={state.agencyPhone}
            agencyWebsite={state.agencyWebsite}
            agencyId={state.agencyId}
            checkingForPreviousData={state.checkingForPreviousData}
            onFullNameChange={(v) => { 
              state.handleFullNameChange(v); 
              if (v.trim().length >= 3) state.clearError('fullName'); 
              else state.setError('fullName', 'Full name must be at least 3 characters');
            }}
            onUsernameChange={(v) => { 
              state.setUsername(v); 
              if (v.trim().length >= 3) state.clearError('username'); 
              else state.setError('username', 'Username must be at least 3 characters');
            }}
            onUsernameEditing={state.setUsernameEditing}
            onEmailChange={(v) => { 
              state.setEmail(v); 
              state.setEmailVerified(false);
              // const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v) && !(/\.([a-zA-Z]{2,})\.\1$/.test(v)) && !(/\.\./.test(v));
              const isValid = emailRegex.test(v);
              if (isValid) {
                state.clearError('email'); 
                state.clearError('emailVerified');
              } else {
                state.setError('email', 'Enter a valid email address');
              }
            }}
            onEmailOtpCodeChange={state.setEmailOtpCode}
            onSendEmailOtp={state.handleSendEmailOtp}
            onVerifyEmailOtp={state.handleVerifyEmailOtp}
            onCloseEmailOtp={state.handleCloseEmailOtp}
            onResendEmailOtp={state.handleResendEmailOtp}
            onEmailVerifiedChange={state.setEmailVerified}
            onBrandNameChange={(v) => { 
              state.setBrandName(v); 
              if (v.trim().length >= 3) state.clearError('brandName'); 
              else state.setError('brandName', 'Brand name must be at least 3 characters');
            }}
            onVerifiedCompanyNameChange={state.setVerifiedCompanyName}
            onWebsiteUrlChange={(v) => { 
              state.handleWebsiteUrlChange(v); 
              state.clearError('websiteUrl'); 
            }}
            onBusinessDescriptionChange={(v) => { 
              state.setBusinessDescription(v); 
              if (v.trim().length >= 10) state.clearError('businessDescription'); 
              else state.setError('businessDescription', 'Business description must be at least 10 characters');
            }}
            onBusinessTypeChange={(v) => { 
              state.setBusinessType(v); 
              if (v) state.clearError('businessType'); 
              else state.setError('businessType', 'Select a business type');
            }}
            onPrimaryCategoryChange={(v) => { 
              state.setPrimaryCategory(v); 
              if (v.trim().length >= 3 && v !== 'Other') state.clearError('primaryCategory'); 
              else if (v === 'Other') state.setError('primaryCategory', 'Enter your custom business category');
              else if (v.trim().length > 0) state.setError('primaryCategory', 'Primary category must be at least 3 characters');
              else state.setError('primaryCategory', 'Primary category is required');
            }}
            onShowCategoryDropdown={state.setShowCategoryDropdown}
            onIsManagedByAgency={(v) => {
              state.setIsManagedByAgency(v);
              if (!v) {
                state.clearError('agencyName');
                state.clearError('agencyEmail');
                state.clearError('agencyContactName');
                state.clearError('agencyPhone');
                state.clearError('agencyWebsite');
                state.clearError('agencyId');
              }
            }}
            onSelectedAgencyFromList={state.setSelectedAgencyFromList}
            onShowAgencyDropdown={state.setShowAgencyDropdown}
            onAgencyNotListed={(v) => {
              state.setAgencyNotListed(v);
              if (!v) {
                state.clearError('agencyName');
                state.clearError('agencyEmail');
                state.clearError('agencyContactName');
                state.clearError('agencyPhone');
                state.clearError('agencyWebsite');
              }
            }}
            onAgencyName={(v) => { 
              state.setAgencyName(v); 
              if (v.trim().length >= 3) state.clearError('agencyName'); 
              else if (v.trim().length > 0) state.setError('agencyName', 'Agency name must be at least 3 characters');
              else state.setError('agencyName', 'Agency name is required');
            }}
            onAgencyId={(v) => { 
              state.setagencyId(v); 
              if (v.trim().length > 0) state.clearError('agencyId'); 
              else state.setError('agencyId', 'Please select an agency');
            }}
            onAgencyContactName={(v) => { 
              state.setAgencyContactName(v); 
              if (v.trim().length >= 3) state.clearError('agencyContactName'); 
              else if (v.trim().length > 0) state.setError('agencyContactName', 'Contact person name must be at least 3 characters');
              else state.setError('agencyContactName', 'Contact person name is required');
            }}
            onAgencyEmail={(v) => { 
              state.setAgencyEmail(v); 
              // const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v) && !(/\.([a-zA-Z]{2,})\.\1$/.test(v)) && !(/\.\./.test(v));
              const isValid = emailRegex.test(v);
              if (isValid) state.clearError('agencyEmail'); 
              else state.setError('agencyEmail', 'Enter a valid agency email');
            }}
            onAgencyPhone={(v) => { 
              state.setAgencyPhone(v); 
              if (!v.trim()) {
                state.setError('agencyPhone', 'Agency phone number is required');
              } else {
                const isValid = phoneRegex.test(v);
                if (isValid) state.clearError('agencyPhone'); 
                else state.setError('agencyPhone', 'Please enter a valid phone number with country code');
              }
            }}
            onAgencyWebsite={(v) => { 
              state.setAgencyWebsite(v); 
              // const isValid = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)$/.test(v);
              const isValid = isValidStrictUrl(v);
              if (isValid || !v.trim()) state.clearError('agencyWebsite'); 
              else state.setError('agencyWebsite', 'Enter a valid website URL');
            }}
            onRegenerateUsername={() => {
              state.setUsernameEditing(true);
              const generated = state.generateUsername(state.fullName || "user");
              state.setUsername(generated);
              if (generated.length >= 3) state.clearError('username');
            }}
          />
        );

      case 2:
        return (
          <StepEcosystem
            errors={state.errors}
            businessReach={state.businessReach}
            nationalOption={state.nationalOption}
            nationalCityInput={state.nationalCityInput}
            nationalCities={state.nationalCities}
            showNationalDropdown={state.showNationalDropdown}
            localLocations={state.localLocations}
            localLocationInput={state.localLocationInput}
            showLocalDropdown={state.showLocalDropdown}
            audienceType={state.audienceType}
            personaDescription={state.personaDescription}
            targetAgeRange={state.targetAgeRange}
            targetGender={state.targetGender}
            interests={state.interests}
            interestInput={state.interestInput}
            professions={state.professions}
            professionInput={state.professionInput}
            physicalPresence={state.physicalPresence}
            competitors={state.competitors}
            visibleCompetitorCount={state.visibleCompetitorCount}
            onShowMoreCompetitors={() =>
              state.setVisibleCompetitorCount((prev) =>
                Math.min(prev + 1, state.competitors.length),
              )
            }
            onBusinessReachChange={(v) => {
              state.handleBusinessReachChange(v);
              if (v) state.clearError('businessReach');
            }}
            onNationalOptionChange={state.setNationalOption}
            onNationalCityInputChange={state.setNationalCityInput}
            onShowNationalDropdown={state.setShowNationalDropdown}
            onAddNationalCity={state.addNationalCity}
            onRemoveNationalCity={state.removeNationalCity}
            onLocalLocationInputChange={state.setLocalLocationInput}
            onShowLocalDropdown={state.setShowLocalDropdown}
            onAddLocalLocation={state.addLocalLocation}
            onRemoveLocalLocation={state.removeLocalLocation}
            onAudienceTypeChange={(v) => {
              state.setAudienceType(v);
              if (v) state.clearError('audienceType');
            }}
            onPersonaDescriptionChange={state.setPersonaDescription}
            onTargetAgeRangeChange={state.setTargetAgeRange}
            onTargetGenderChange={state.setTargetGender}
            onInterestInputChange={state.setInterestInput}
            onAddInterest={state.addInterest}
            onRemoveInterest={state.removeInterest}
            onProfessionInputChange={state.setProfessionInput}
            onAddProfession={state.addProfession}
            onRemoveProfession={state.removeProfession}
            onPhysicalPresenceChange={(v) => {
              state.setPhysicalPresence(v);
              if (v) state.clearError('physicalPresence');
            }}
            onUpdateCompetitor={state.updateCompetitor}
            onRemoveCompetitor={state.handleRemoveCompetitor}
          />
        );

      case 3:
        return (
          <StepBusinessPortfolio
            errors={state.errors}
            userId={state.userId}
            businessType={state.businessType}
            products={state.products}
            services={state.services}
            differentiators={state.differentiators}
            socialPlatforms={state.socialPlatforms}
            brandLogo={state.brandLogo}
            primaryFont={state.primaryFont}
            secondaryFont={state.secondaryFont}
            homePageUrl={state.homePageUrl}
            productPageUrl={state.productPageUrl}
            servicePageUrl={state.servicePageUrl}
            contactPageUrl={state.contactPageUrl}
            aboutPageUrl={state.aboutPageUrl}
            blogPageUrl={state.blogPageUrl}
            customPageUrls={state.customPageUrls}
            primaryColor={state.primaryColor}
            secondaryColor={state.secondaryColor}
            showPrimaryFontDropdown={state.showPrimaryFontDropdown}
            showSecondaryFontDropdown={state.showSecondaryFontDropdown}
            onAddProduct={state.addProduct}
            onUpdateProduct={state.updateProduct}
            onRemoveProduct={state.removeProduct}
            onToggleProductPriority={state.toggleProductPriority}
            onAddService={state.addService}
            onUpdateService={state.updateService}
            onRemoveService={state.removeService}
            onToggleServicePriority={state.toggleServicePriority}
            onAddDifferentiator={state.addDifferentiator}
            onUpdateDifferentiator={state.updateDifferentiator}
            onUpdateSocialPlatform={state.updateSocialPlatform}
            onLogoUpload={state.handleLogoUpload}
            onPrimaryFontChange={state.setPrimaryFont}
            onSecondaryFontChange={state.setSecondaryFont}
            onShowPrimaryFontDropdown={state.setShowPrimaryFontDropdown}
            onShowSecondaryFontDropdown={state.setShowSecondaryFontDropdown}
            onHomePageUrlChange={(v) => {
              state.setHomePageUrl(v);
              // if (!v.trim() || /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(v)) state.clearError
              if (!v.trim() || isValidStrictUrl(v)) state.clearError('homePageUrl');
              else state.setError('homePageUrl', 'Please enter a valid root domain (e.g., example.com) without paths or deep links');
            }}
            onProductPageUrlChange={(v) => {
              state.setProductPageUrl(v);
              // if (!v.trim() || /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(v)) state.clearError
              if (!v.trim() || isValidStrictUrl(v)) state.clearError('productPageUrl');
              else state.setError('productPageUrl', 'Please enter a valid root domain (e.g., example.com) without paths or deep links');
            }}
            onServicePageUrlChange={(v) => {
              state.setServicePageUrl(v);
              // if (!v.trim() || /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(v)) state.clearError
              if (!v.trim() || isValidStrictUrl(v)) state.clearError('servicePageUrl');
              else state.setError('servicePageUrl', 'Please enter a valid root domain (e.g., example.com) without paths or deep links');
            }}
            onContactPageUrlChange={(v) => {
              state.setContactPageUrl(v);
              // if (!v.trim() || /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(v)) state.clearError
              if (!v.trim() || isValidStrictUrl(v)) state.clearError('contactPageUrl');
              else state.setError('contactPageUrl', 'Please enter a valid root domain (e.g., example.com) without paths or deep links');
            }}
            onAboutPageUrlChange={(v) => {
              state.setAboutPageUrl(v);
              // if (!v.trim() || /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(v)) state.clearError
              if (!v.trim() || isValidStrictUrl(v)) state.clearError('aboutPageUrl');
              else state.setError('aboutPageUrl', 'Please enter a valid root domain (e.g., example.com) without paths or deep links');
            }}
            onBlogPageUrlChange={(v) => {
              state.setBlogPageUrl(v);
              // if (!v.trim() || /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\/?$/.test(v)) state.clearError
              if (!v.trim() || isValidStrictUrl(v)) state.clearError('blogPageUrl');
              else state.setError('blogPageUrl', 'Please enter a valid root domain (e.g., example.com) without paths or deep links');
            }}
            onAddCustomPageUrl={state.addCustomPageUrl}
            onUpdateCustomPageUrl={state.updateCustomPageUrl}
            onRemoveCustomPageUrl={state.removeCustomPageUrl}
            onPrimaryColorChange={state.setPrimaryColor}
            onSecondaryColorChange={state.setSecondaryColor}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      {/* Header */}
      <div className="text-center pt-8 pb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          Brand Onboarding
        </h1>
        <p className="text-sm text-gray-500">
          Set Up Your Brand for Smart Business Growth.
        </p>
      </div>

      {/* Progress Steps */}
      <ProgressBar currentStep={state.currentStep} />

      {/* Form Content */}
      <div className="max-w-2xl mx-auto px-6 pb-12">
        {renderStepContent()}

        {/* Continue / Back Buttons */}
        <div className="flex justify-between items-center mt-10">
          {state.currentStep > 1 && (
            <button
              onClick={state.handleBack}
              className="px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-arrow-left-line"></i>
              Back
            </button>
          )}
          {state.currentStep === 1 && <div></div>}
          <button
            type="button"
            disabled={state.savingData || Object.keys(state.errors).some(key => state.errors[key])}
            onClick={state.handleContinue}
            className={`px-8 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              state.savingData || Object.keys(state.errors).some(key => state.errors[key])
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-teal-500 text-white hover:bg-teal-600 cursor-pointer'
            }`}
          >
            {state.savingData ? (
              <>
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Saving...
              </>
            ) : (
              <>
                {state.currentStep === 3 ? "Complete Setup" : "Continue"}
                <i
                  className={
                    state.currentStep === 3
                      ? "ri-check-line"
                      : "ri-arrow-right-line"
                  }
                ></i>
              </>
            )}
          </button>
        </div>

        {/* Back Link on step 1 */}
        {state.currentStep === 1 && (
          <div className="flex justify-start mt-4">
            <Link
              to="/onboarding"
              className="inline-flex items-center text-gray-500 text-sm hover:text-gray-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              Back to Account Selection
            </Link>
          </div>
        )}
      </div>

      {/* Terms & Conditions Modal */}
      <TermsModal
        termsAndConditionsId={state.termsAndConditionsId}
        email={state.email}
        userId={state.userId}
        open={state.showTermsModal}
        onAccept={state.handleAcceptTerms}
        onClose={() => state.setShowTermsModal(false)}
      />

      {/* Data Recovery Modal */}
      <DataRecoveryModal
        open={state.showRecoveryModal}
        data={state.pendingRecoveryData}
        onAccept={state.handleAcceptRecovery}
        onDecline={state.handleDeclineRecovery}
        isDeleting={state.isDeletingTempData}
      />

      {/* Unified User Status Modal */}
      {(state.showEmailExistsModal || state.showRegistrationRequiredModal) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md rounded-2xl p-6 shadow-xl relative overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className={`absolute top-0 left-0 right-0 h-2 ${state.showEmailExistsModal ? 'bg-amber-500' : 'bg-teal-500'}`}></div>
            
            <div className="flex flex-col items-center text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${state.showEmailExistsModal ? 'bg-amber-100' : 'bg-teal-50'}`}>
                <i className={`${state.showEmailExistsModal ? 'ri-error-warning-fill text-amber-500' : 'ri-user-add-line text-teal-600'} text-3xl`}></i>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {state.showEmailExistsModal ? "Email Already Exists" : "Registration Required"}
              </h3>
              <p className="text-sm text-gray-600 mb-8">
                {state.showEmailExistsModal 
                  ? (state.emailExistsMessage || "This email is already associated with an account. Would you like to log in instead?")
                  : "It looks like you haven't completed your registration yet. Please register first before starting the onboarding process."}
              </p>
              
              <div className="flex gap-3 w-full">
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.removeItem("email");
                    state.setEmail("");
                    if (state.showEmailExistsModal) state.setShowEmailExistsModal(false);
                    if (state.showRegistrationRequiredModal) state.setShowRegistrationRequiredModal(false);
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                {state.showEmailExistsModal ? (
                  <Link
                    to="/?login=true"
                    className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    Login
                  </Link>
                ) : (
                  <a
                    href={state.registrationRedirectUrl || "/register"}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    Register Now
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global Loader for Data Check */}
      {state.checkingForPreviousData && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="flex flex-col items-center gap-5 p-10 rounded-3xl bg-white shadow-2xl border border-gray-100 scale-95 animate-in zoom-in duration-300">
            <div className="relative">
              <div className="w-20 h-20 border-[5px] border-teal-50 border-t-teal-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="ri-database-2-line text-teal-600 text-2xl animate-pulse"></i>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-1.5">
                Checking for previous data
              </h3>
              <p className="text-sm text-gray-500 font-medium">
                Please wait while we look for your progress...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
